import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SessionProvider, useSession } from '../context/SessionContext';
import { getQuiz } from '../services/quizService';
import QuestionDisplay from '../components/display/QuestionDisplay';
import Timer from '../components/common/Timer';
import Scoreboard from '../components/display/Scoreboard';
import QRCode from '../components/common/QRCode';

function DisplayContent() {
  const { session, loading, error } = useSession();
  const [quiz, setQuiz] = useState(null);
  const [showScoreboard, setShowScoreboard] = useState(false);

  // Fetch quiz when session is loaded
  useEffect(() => {
    const fetchQuiz = async () => {
      if (session?.quizId) {
        try {
          const quizData = await getQuiz(session.quizId);
          setQuiz(quizData);
        } catch (err) {
          console.error('Error fetching quiz:', err);
        }
      }
    };

    fetchQuiz();
  }, [session?.quizId]);

  // Reset scoreboard when question changes
  useEffect(() => {
    if (session?.status === 'active') {
      setShowScoreboard(false);
    }
  }, [session?.currentQuestionIndex]);

  const handleTimerExpire = () => {
    setShowScoreboard(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl font-bold mb-4">Lastar...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-pink-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl font-bold mb-4">Feil</div>
          <div className="text-2xl">{error}</div>
        </div>
      </div>
    );
  }

  // Waiting state
  if (session?.status === 'waiting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-6xl font-bold mb-8">{quiz?.title || 'Kviss'}</h1>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-8">
            <div className="bg-white text-purple-600 rounded-2xl p-8">
              <div className="text-2xl font-semibold mb-2">PIN-kode</div>
              <div className="text-7xl font-bold tracking-wider">{session.id}</div>
            </div>
            <div className="text-3xl hidden md:block">eller</div>
            <QRCode sessionId={session.id} size={200} lightText={true} />
          </div>
          <p className="text-3xl">Ventar på at verten skal starte...</p>
        </div>
      </div>
    );
  }

  // Active state
  if (session?.status === 'active') {
    const currentQuestion = quiz?.questions?.[session.currentQuestionIndex];

    if (!currentQuestion) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-4xl font-bold">Lastar spørsmål...</div>
          </div>
        </div>
      );
    }

    // Show scoreboard after timer expires
    if (showScoreboard) {
      return (
        <Scoreboard
          participants={session.participants || []}
          question={currentQuestion}
          showCorrectAnswer={true}
        />
      );
    }

    // Show question with timer
    return (
      <div className="relative">
        <QuestionDisplay
          question={currentQuestion}
          questionNumber={session.currentQuestionIndex + 1}
          totalQuestions={quiz.questions.length}
        />
        <Timer
          startTime={session.questionStartTime}
          duration={currentQuestion.timeLimit || 30}
          onExpire={handleTimerExpire}
        />
      </div>
    );
  }

  // Finished state
  if (session?.status === 'finished') {
    return (
      <Scoreboard
        participants={session.participants || []}
        showCorrectAnswer={false}
      />
    );
  }

  // Default fallback
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
      <div className="text-center text-white">
        <h1 className="text-6xl font-bold mb-4">Visningsside</h1>
        <p className="text-2xl">Ukjend øktstatus</p>
      </div>
    </div>
  );
}

export default function DisplayPage() {
  const { sessionId } = useParams();

  return (
    <SessionProvider sessionId={sessionId}>
      <DisplayContent />
    </SessionProvider>
  );
}
